import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, getUserPhone } from '../../lib/api';
import GiaBreathing from './GiaBreathing';

const MOODS = [
  { v: 1, e: '😞', l: 'Drained' },
  { v: 2, e: '😕', l: 'Low' },
  { v: 3, e: '😐', l: 'Okay' },
  { v: 4, e: '🙂', l: 'Good' },
  { v: 5, e: '😄', l: 'Great' },
];

const MOOD_COLOR = ['#f87171', '#fb923c', '#fbbf24', '#a3e635', '#34d399'];

export default function GiaWellnessHome({ firstName, onTalk, onBurnout }) {
  const [picked, setPicked] = useState(null);
  const [trend, setTrend] = useState([]);
  const [showBreath, setShowBreath] = useState(false);

  const loadTrend = () => {
    const phone = getUserPhone();
    if (!phone) return;
    axios.get(`${API}/api/gia/mood/${encodeURIComponent(phone)}`)
      .then((r) => setTrend((r.data.entries || []).reverse()))
      .catch(() => {});
  };
  useEffect(loadTrend, []);

  const pickMood = async (m) => {
    setPicked(m.v);
    try {
      await axios.post(`${API}/api/gia/mood`, { phone: getUserPhone() || 'anon', mood: m.v });
      toast.success('Mood noted — thank you for checking in');
      loadTrend();
    } catch { /* soft fail */ }
    if (m.v <= 2) {
      setTimeout(() => onTalk(`I'm feeling ${m.l.toLowerCase()} today. Can we talk?`), 900);
    }
  };

  const tools = [
    { icon: '💬', title: 'Talk to Gia', sub: 'Private, judgement-free conversation', testId: 'gia-tool-talk', onClick: () => onTalk(null) },
    { icon: '🔥', title: 'Burnout Self-Check', sub: '8 questions · know your strain level', testId: 'gia-tool-burnout', onClick: onBurnout },
    { icon: '🫁', title: 'Breathing Reset', sub: '2 minutes to calm your system', testId: 'gia-tool-breathing', onClick: () => setShowBreath((s) => !s) },
    { icon: '🌙', title: 'Night-Duty Wind-Down', sub: 'Decompress after a tough shift', testId: 'gia-tool-winddown', onClick: () => onTalk('I just finished a night duty. Guide me through winding down so I can rest properly.') },
    { icon: '✍️', title: 'Daily Reflection', sub: 'One minute of gratitude & release', testId: 'gia-tool-reflection', onClick: () => onTalk('Guide me through a one-minute daily reflection: one thing that went well today, one thing I want to let go of.') },
  ];

  return (
    <main className="relative z-10 max-w-2xl mx-auto px-5 pb-20 pt-8" data-testid="gia-wellness-home">
      <div className="flex items-center gap-2 mb-3 text-purple-300 text-xs font-bold uppercase tracking-widest">
        ✦ Mental Wellness · For Doctors
      </div>
      <h1 className="text-4xl font-black text-white leading-[1.08] tracking-tight mb-2">
        {firstName ? `Hey ${firstName},` : 'Hey Doctor,'}<br />
        <span style={{ background: 'linear-gradient(120deg,#c4b5fd,#f0abfc,#f472b6)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
          how are you, really?
        </span>
      </h1>
      <p className="text-white/60 text-sm mb-7">Your complete mental wellness space — check in, reset, and talk it out. Everything stays private.</p>

      {/* Mood check-in */}
      <div className="rounded-3xl p-5 border border-white/10 mb-4" style={{ background: 'rgba(255,255,255,0.05)' }} data-testid="gia-mood-card">
        <p className="text-[10px] font-black tracking-[0.25em] uppercase text-white/50 mb-3">Mood check-in</p>
        <div className="flex justify-between gap-1">
          {MOODS.map((m) => (
            <button
              key={m.v}
              onClick={() => pickMood(m)}
              data-testid={`gia-mood-${m.v}`}
              className={`flex-1 py-3 rounded-2xl text-center transition-transform border ${
                picked === m.v ? 'border-purple-300/70 bg-purple-500/25 scale-105' : 'border-white/10 bg-white/5 hover:bg-white/10'
              }`}
            >
              <span className="text-2xl block">{m.e}</span>
              <span className="text-[10px] font-bold text-white/60 mt-1 block">{m.l}</span>
            </button>
          ))}
        </div>
        {trend.length > 0 && (
          <div className="mt-4 pt-3 border-t border-white/10" data-testid="gia-mood-trend">
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 mb-2">Your last check-ins</p>
            <div className="flex items-end gap-1.5 h-10">
              {trend.map((t, i) => (
                <div key={i} className="flex-1 max-w-[36px] rounded-t-md" title={new Date(t.created_at).toLocaleDateString()} style={{ height: `${t.mood * 20}%`, background: MOOD_COLOR[t.mood - 1], opacity: 0.85 }} />
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Tools */}
      <div className="space-y-2.5">
        {tools.map((t) => (
          <button
            key={t.testId}
            onClick={t.onClick}
            data-testid={t.testId}
            className="w-full flex items-center gap-4 rounded-2xl px-4 py-4 border border-white/10 text-left hover:border-purple-300/40 hover:bg-purple-500/10 transition-colors"
            style={{ background: 'rgba(255,255,255,0.04)' }}
          >
            <span className="w-11 h-11 rounded-2xl flex items-center justify-center text-xl shrink-0" style={{ background: 'rgba(168,85,247,0.18)' }}>{t.icon}</span>
            <span className="flex-1 min-w-0">
              <span className="block text-sm font-black text-white">{t.title}</span>
              <span className="block text-xs text-white/50 mt-0.5">{t.sub}</span>
            </span>
            <span className="text-purple-300/70">→</span>
          </button>
        ))}
      </div>

      {showBreath && (
        <div className="mt-4 rounded-3xl p-5 border border-purple-300/20" style={{ background: 'rgba(168,85,247,0.08)' }}>
          <GiaBreathing minutes={2} />
        </div>
      )}

      <p className="text-[10px] text-white/40 text-center mt-8">
        🔒 Private · not a medical or psychiatric service · if in crisis, call iCall 9152987821 (India)
      </p>
    </main>
  );
}
